// ---------------------------------------------------------------------------
// SHARED ASSESSMENT HISTORY HELPERS
// ---------------------------------------------------------------------------
// Used by start-assessment.js and Review-assessment.js so both pathways
// resolve the inheritance source and the "Previous Data" panel identically.
//
// get_historical_pathway_data.php returns:
//   {
//     status: true,
//     client_id, client_name, age_group,
//     group_name,
//     groups: ["Demographics", "Background Information & Assessments", ...],
//     data: [ { session_id, appointment_type, assessment_date, status,
//               pathway: [ { node, question, keyword, type, answer, options } ] } ]
//   }
// Sessions are ordered oldest -> newest server side.
// ---------------------------------------------------------------------------

const toTime = (value) => {
    const t = Date.parse(value || "");
    return Number.isNaN(t) ? 0 : t;
};

const normaliseType = (value) => String(value == null ? "" : value).trim().toLowerCase();

export const isReviewAppointment = (value) => normaliseType(value) !== "initial" && normaliseType(value) !== "";

// Column/section labels for the Previous Data tables. Kept as a plain
// capitaliser rather than pulling in the prefix wording, because a table cell
// wants the short form ("Initial" / "Review") and a group header wants the long
// one ("Initial Appointment - ...").
export const isInitialAppointmentType = (value) => !isReviewAppointment(value);

export const formatAppointmentLabel = (value) => {
    const v = String(value == null ? "" : value).trim();
    if (!v) return "-";
    // Review Smart Goal is its own appointment type, not a plain Review, and it
    // runs as a separate pass in the client's history - reading it as "Review"
    // collapsed two different columns into one in the Previous Data tables.
    if (v.toLowerCase() === "review smart goal") return "Review Smart Goal";
    return isInitialAppointmentType(v) ? "Initial" : "Review";
};

// A session counts as "completed enough to inherit from" when at least one
// question in it actually carries an answer. HINE/HAI answers arrive as
// { value, entries } so both shapes are checked.
const hasAnsweredQuestions = (session) => {
    const pathway = Array.isArray(session?.pathway) ? session.pathway : [];
    return pathway.some((q) => {
        const answer = q?.answer;
        if (answer === null || answer === undefined || answer === "") return false;
        if (Array.isArray(answer)) return answer.length > 0;
        if (typeof answer === "object") {
            if (answer.value) return true;
            return Array.isArray(answer.entries) && answer.entries.length > 0;
        }
        return true;
    });
};

// ---------------------------------------------------------------------------
// Data Continuity: pick the session a new Review inherits its data from.
//
// Spec: "for a Review appointment, get the LATEST data". The source is
// therefore the most recent answered session on record for this client (newest
// first, of any appointment type - Initial, Review or Review Smart Goal),
// restricted to the sessions that come BEFORE the one being answered and, when
// the caller knows the band, to that same age band.
//
// The old rule rated the appointment TYPE above recency (previous Review,
// else Initial, else Review Smart Goal), which is what left Reviews opening on
// stale data - an interleaved Review Smart Goal, or any session answered more
// recently than the last Review, was skipped over.
//
// Review Smart Goal never reaches this fallback through its own entry point:
// goalreview passes ?goalSessionId and the caller resolves that EXACT session
// first, so an RSG always reads the session the therapist clicked.
//
// `excludeSessionId` is the session currently being answered. It MUST be
// excluded: get_historical_pathway_data.php returns every session for the
// client, and because it has no "created_at" it is ordered by started_at, so
// the session being edited is almost always the newest row in the list. Without
// this guard the "newest answered session" is the session itself, and the
// pre-fill copies the draft's own partially-answered questions back onto
// itself instead of inheriting anything - which also freezes answers the
// therapist has since changed.
//
// Excluding the current session is NECESSARY BUT NOT SUFFICIENT. The client's
// history routinely holds more than one appointment that is newer than the one
// being answered - a second Review drafted and abandoned while the first is
// still open, or any later appointment. Picking "the newest answered review"
// therefore reached FORWARD in time: resuming the first Review inherited the
// abandoned second Review, and re-answering an older Review inherited data that
// did not exist yet when it was written. Only sessions that come BEFORE the one
// being answered may be a source, so the candidate list is truncated at that
// point rather than merely having one entry removed.
//
// When the session being answered is absent from the history - a brand-new
// appointment that has not been created yet - there is nothing to truncate at
// and the whole list stays eligible.
// ---------------------------------------------------------------------------
export const pickInheritanceSession = (history, excludeSessionId = null, ageGroup = null) => {
    const sessions = Array.isArray(history?.data) ? history.data : [];
    if (sessions.length === 0) return null;

    const idOf = (s) => String(s?.session_id ?? s?.sessionId ?? "");
    const excluded = excludeSessionId != null && excludeSessionId !== ""
        ? String(excludeSessionId)
        : null;

    // Truncate at the session being answered: only its predecessors are eligible.
    // Falling back to "everything but the current session" keeps a not-yet-created
    // appointment inheriting from the client's most recent one.
    let boundary = excluded === null ? sessions.length : sessions.findIndex(s => idOf(s) === excluded);
    if (boundary < 0) boundary = sessions.length;

    let candidates = sessions.slice(0, boundary);

    if (candidates.length === 0) return null;

    // SAME-PATHWAY GATE.
    //
    // A session's questions come from its pathway, which is chosen by the client's
    // age band. Inheriting across bands therefore copies answers between two
    // different question sets, and one bad session then poisons every later
    // appointment in the chain, because each new appointment inherits from the
    // newest one and the bad session stays the newest.
    //
    // Observed: client "First Oct three" is registered 06-12 months, but one
    // Review was saved against 02-03 months (clpa 221). The next Review
    // (session 100002, clpa 123) inherited from that clpa-221 session and
    // received its goal domain verbatim, comma-joined and unreadable, as
    // "Motor,Communication and Social,Participation" - even though the session
    // immediately before it (100000) had a perfectly good selection.
    //
    // Only narrow when the caller knows the band AND at least one candidate
    // carries one. A missing/null age_group (older payloads) must not wipe the
    // whole candidate list, or nothing would ever be inherited.
    const wantAge = String(ageGroup == null ? "" : ageGroup).trim();
    if (wantAge) {
        const sameAge = candidates.filter(s => {
            const a = String(s?.age_group == null ? "" : s.age_group).trim();
            return a !== "" && a.toLowerCase() === wantAge.toLowerCase();
        });
        if (sameAge.length > 0) candidates = sameAge;
    }

    const answered = candidates.filter(hasAnsweredQuestions);
    if (answered.length > 0) {
        const byNewest = (list) => [...list].sort((a, b) => toTime(a.assessment_date) - toTime(b.assessment_date));

        // REVIEW = LATEST DATA (therapist's spec).
        //
        // A new Review must show the client's LATEST recorded data, so the
        // source is simply the most recent answered session - whichever
        // appointment type it carries. The old chain (newest Review, else
        // newest Initial, else newest Review Smart Goal) deliberately rated
        // the appointment TYPE above recency, and that is what made a Review
        // open on stale data: with Initial -> RSG on record and no earlier
        // Review, the chain fell back to the Initial and skipped the newer
        // RSG, and on the same band it could reach back past sessions that
        // had been answered more recently.
        //
        // Review Smart Goal does NOT read this branch: start-assessment /
        // Review-assessment resolve the exact session the therapist clicked
        // (?goalSessionId) before this fallback is ever consulted.
        //
        // `byNewest` sorts ascending, so the last entry is the latest one.
        return byNewest(answered)[answered.length - 1];
    }

    const initials = candidates.filter((s) => normaliseType(s.appointment_type) === "initial");
    return initials.length > 0 ? initials[initials.length - 1] : null;
};

// ---------------------------------------------------------------------------
// Keyword -> inherited-answer resolution.
//
// An Initial and its Reviews never ask the same question in the same words, so
// matching on question text alone carries nothing across. The `keywords` column
// is the stable link between the two ("Neuroimaging" on the Initial,
// "Neuroimaging results" on the Review), but it is authored by hand and drifts:
// filler words get added, qualifiers get dropped.
//
// Resolution is therefore tiered, and every tier returns null rather than
// guessing, so an inherited answer is either certainly the right question's or
// absent:
//
//   1. exact keyword, group-scoped then global
//   2. normalised keyword (filler words dropped), group-scoped then global
//   3. normalised keyword containment, group-scoped then global
//
// The group prefix is what keeps the "Communication" comorbidity (group 3) from
// being matched by the "Communication" motor skill (group 2).
// ---------------------------------------------------------------------------

// Words that only qualify a keyword and carry no identity of their own, so
// "Neuroimaging results" and "Neuroimaging" normalise to the same key.
const KEYWORD_FILLER_WORDS = new Set([
    "result", "results", "available", "availability", "updated", "update", "new",
    "any", "current", "latest", "recent", "information", "info", "score", "scores",
]);

// The catalogue spells ONE question three different ways across appointment
// types, and the abbreviation is short enough that the containment tier below
// will never reach it (MIN_FUZZY_KEYWORD_LEN = 6, "comm" is 4). With no alias the
// shortened spellings fall through to a DIFFERENT question that happens to share
// the long spelling, and the pre-fill writes that question's answer onto this
// one. Observed on every Initial -> Review hand-off:
//
//   122.2.7  keyword "Comm"                (Communication SKILL)
//   122.3.8  keyword "Communication"       (communication COMORBIDITY)
//   123.2.8  keyword "Communication"       <- the skill question in Review
//   123.3.9  keyword "Communication"       <- the comorbidity question in Review
//   220.2.8  keyword "Communication skill" (the skill question in another Review)
//
// So the Review's "Communication" skill resolved to the Initial's "Communication"
// COMORBIDITY, and a skill level (Limited / Emerging / Established) was prefilled
// with "Yes". Folding the three spellings onto one key fixes it properly: the two
// questions stay distinct because they sit in different node groups, and the
// group-scoped lookup - which is always tried first - then picks the right one.
//
// Same story for the Play question: Initial says "Play", Review says
// "Play/Cognition", and "play" is again under the containment floor.
//
// These are catalogue facts, not guesses. A new spelling must be added here
// explicitly rather than widening MIN_FUZZY_KEYWORD_LEN: lowering the floor
// matches any short keyword that is a substring of anything else, which was
// measured to pull "Goal Domain" onto "Would you like to revise this goal" and
// "Goal Status" onto "Update & Close the 'Achieved' Goal".
const KEYWORD_ALIASES = new Map([
    ["comm", "communication"],
    ["communication skill", "communication"],
    ["play", "play cognition"],
    ["neuroimaging results", "neuroimaging"],
]);

export const normaliseKeyword = (raw) => {
    const tokens = String(raw == null ? "" : raw)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .filter((w) => !KEYWORD_FILLER_WORDS.has(w));
    const key = tokens.join(" ").trim();
    return KEYWORD_ALIASES.get(key) || key;
};

// Shortest form accepted by the containment tier, so a short abbreviation can
// never hijack an unrelated question. Long-enough aliases live in
// KEYWORD_ALIASES instead of lowering this number - see the note there.
const MIN_FUZZY_KEYWORD_LEN = 6;

const pathwayOf = (sourceSession) => (Array.isArray(sourceSession?.pathway) ? sourceSession.pathway : []);

const groupOfNode = (node) => {
    const parts = String(node || "").split(".");
    return parts.length > 1 ? parts[1] : "";
};

// Builds the resolver used to carry an answer from the source session over to a
// question in the current pathway. Returns a function taking
// (rawKeyword, nodeGroup) and yielding the matching source question or null.
export const buildKeywordLookup = (sourceSession) => {
    const pathway = pathwayOf(sourceSession);

    // This used to be two maps - tier 1 keyed on the raw lower-cased keyword,
    // tier 2 on the normalised one - and the split was the bug: the Review's
    // "Communication" matched the Initial's "Communication" COMORBIDITY on tier 1
    // before tier 2 could offer the "Comm" SKILL. One normalised key space now.
    const byKeyword = new Map();
    // A bare (unscoped) key is stored only when that keyword is UNIQUE in the
    // source pathway, so an ambiguous keyword can never silently pick a winner.
    // Scoping still decides the ambiguous cases, and it decides them correctly
    // where the two node groups mean the same thing on both sides - "Communication"
    // the skill (group 2) and "Communication" the comorbidity (group 3) hold on
    // the Initial and on both Review spellings.
    const keywordCount = new Map();

    pathway.forEach((q) => {
        const norm = normaliseKeyword(q.keyword);
        if (norm) keywordCount.set(norm, (keywordCount.get(norm) || 0) + 1);
    });

    pathway.forEach((q) => {
        if (!q.keyword) return;
        const norm = normaliseKeyword(q.keyword);
        if (!norm) return;
        const group = groupOfNode(q.node);
        if (group) byKeyword.set(group + "||" + norm, q);
        if ((keywordCount.get(norm) || 0) === 1 && !byKeyword.has(norm)) byKeyword.set(norm, q);
    });

    // Tier 3: containment, for trailing qualifiers the normaliser cannot drop.
    // Restricted to the same group first, and accepted only when it identifies a
    // single question.
    const containment = (norm, nodeGroup) => {
        const prefix = nodeGroup ? nodeGroup + "||" : "";
        const candidates = [];
        byKeyword.forEach((q, key) => {
            if (prefix && !key.startsWith(prefix)) return;
            const candidate = key.slice(prefix.length);
            if (candidate === norm) return;
            if (candidate.length < MIN_FUZZY_KEYWORD_LEN) return;
            if (norm.includes(candidate) || candidate.includes(norm)) candidates.push(q);
        });
        return candidates.length === 1 ? candidates[0] : null;
    };

    return (rawKeyword, nodeGroup) => {
        const norm = normaliseKeyword(rawKeyword);
        if (!norm) return null;
        if (nodeGroup) {
            const scoped = byKeyword.get(nodeGroup + "||" + norm);
            if (scoped) return scoped;
        }
        const global = byKeyword.get(norm);
        if (global) return global;
        return containment(norm, nodeGroup);
    };
};

// ---------------------------------------------------------------------------
// Group-name prefix: the group headers are named after the appointment that
// produced them, so a Review question never reads as if it were an Initial one.
// Wording lives here so it can be retuned in one place.
// ---------------------------------------------------------------------------
const APPOINTMENT_GROUP_PREFIX = {
    initial: "Initial Appointment",
    review: "Review Appointment",
};

export const groupPrefixForAppointment = (appointment) =>
    APPOINTMENT_GROUP_PREFIX[isReviewAppointment(appointment) ? "review" : "initial"];

export const withAppointmentPrefix = (groupName, appointment) => {
    const name = String(groupName || "").trim();
    if (!name) return name;
    return `${groupPrefixForAppointment(appointment)} - ${name}`;
};

// ---------------------------------------------------------------------------
// Group list for the "Previous Data" file bar. Demographics is hoisted to the
// front because it is the entry point most therapists open first; the rest
// keep their pathway order.
// ---------------------------------------------------------------------------
export const orderHistoryGroups = (history, pathwayGroups) => {
    const fromApi = Array.isArray(history?.groups) ? history.groups : [];
    const fromPathway = Array.isArray(pathwayGroups) ? pathwayGroups : [];

    // UNION of both sources, history first.
    //
    // The history list spans every pathway the client has been assessed on, but
    // the bar is opened from inside a Review session, and a Review pathway
    // carries groups the Initial one does not (e.g. "Goal Progression"). Taking
    // only the history list hid those, and taking only the active pathway hid
    // the groups the client reached in earlier sessions. Merging keeps both.
    // Deduplicated case-insensitively, first occurrence wins.
    const seen = new Set();
    const groups = [...fromApi, ...fromPathway]
        .map((g) => (typeof g === "string" ? { group_name: g } : g))
        .filter((g) => {
            if (!g || !g.group_name) return false;
            const key = normaliseType(g.group_name);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });

    const isDemographics = (g) => normaliseType(g.group_name) === "demographics";
    const demographics = groups.filter(isDemographics);
    const others = groups.filter((g) => !isDemographics(g));
    if (demographics.length > 0) return [...demographics, ...others];

    // "Demographics" is a virtual group and is deliberately always offered.
    //
    // It is not a row in the `pathways` table, so get_historical_pathway_data.php
    // can never list it in `groups` - and the file bar is built from that list,
    // which is why the group's own data (appointment type / client / client id /
    // age group for each past session) had no way to be reached. The API DOES
    // answer a group_name = "Demographics" request with one row per past
    // session, and start-assessment renders those rows in a dedicated table, so
    // the entry is prepended here and hoisted to the front as the usual entry
    // point.
    return [{ group_name: "Demographics" }, ...others];
};

// ---------------------------------------------------------------------------
// Answer formatter shared by the inline Previous Data tables.
// ---------------------------------------------------------------------------
export const formatHistoryAnswer = (answer) => {
    if (answer === null || answer === undefined || answer === "") return "-";
    if (typeof answer === "object") {
        if (Array.isArray(answer)) {
            return answer.map((a) => formatHistoryAnswer(a)).filter((a) => a !== "-").join(", ") || "-";
        }
        if (Array.isArray(answer.entries)) {
            return answer.entries.map((e) => JSON.stringify(e)).join(" | ");
        }
        if (answer.value) return String(answer.value);
        return JSON.stringify(answer);
    }
    return String(answer);
};
